import { api } from './api';
import type { ChannelType, UserChannel, UserNotificationSettings } from '@/types';

/**
 * API-клиент управления каналами уведомлений (notification-fitness).
 * Все эндпоинты требуют JWT — userId определяется на бэке из токена.
 *
 * Базовый URL notificationApi = /api/notifications, поэтому пути здесь —
 * относительно него (channels/**, settings/**).
 */
export const channelService = {
  /** Список каналов пользователя (включая неподтверждённые), отсортированные по приоритету. */
  async listChannels(): Promise<UserChannel[]> {
    const response = await api.notification.get<UserChannel[]>('/channels');
    return response.data;
  },

  /** Инициировать верификацию email-канала: бэкенд шлёт 6-значный код на адрес. */
  async requestEmailVerification(email: string): Promise<void> {
    await api.notification.post('/channels/email', { email });
  },

  /** Подтвердить email-канал 6-значным кодом. */
  async verifyEmail(email: string, code: string): Promise<void> {
    await api.notification.post('/channels/email/verify', { email, code });
  },

  /** Получить deep-link для привязки Telegram (t.me/<bot>?start=<token>). */
  async linkTelegram(): Promise<string> {
    const response = await api.notification.post<{ deepLink: string }>('/channels/telegram/link');
    return response.data.deepLink;
  },

  /** Удалить канал. */
  async deleteChannel(channelType: ChannelType): Promise<void> {
    await api.notification.delete(`/channels/${channelType}`);
  },

  /** Переупорядочить приоритеты подтверждённых каналов. */
  async updatePriorities(
    priorities: { channelType: ChannelType; priority: number }[]
  ): Promise<UserChannel[]> {
    const response = await api.notification.put<UserChannel[]>('/channels/priorities', {
      priorities,
    });
    return response.data;
  },

  // ─── WebPush (multi-device, требование 4) ───────────────────────────

  /** Зарегистрировать браузерную push-подписку (это устройство). */
  async subscribePush(subscription: {
    endpoint: string;
    p256dh: string;
    auth: string;
    userAgent?: string;
  }): Promise<void> {
    await api.notification.post('/channels/push/subscribe', subscription);
  },

  /** Отписать это устройство (по endpoint). multi-device: другие остаются. */
  async unsubscribePush(endpoint: string): Promise<void> {
    await api.notification.post('/channels/push/unsubscribe', { endpoint });
  },

  /** Сколько устройств зарегистрировано для push. */
  async pushDeviceCount(): Promise<number> {
    const response = await api.notification.get<{ count: number }>('/channels/push/count');
    return response.data.count;
  },

  /** Публичный VAPID-ключ для PushManager.subscribe(). */
  async getVapidPublicKey(): Promise<string> {
    const response = await api.notification.get<{ publicKey: string }>(
      '/channels/push/vapid-public-key'
    );
    return response.data.publicKey;
  },

  /** Загрузить per-action настройки (типы событий). */
  async getSettings(userId: string): Promise<UserNotificationSettings> {
    const response = await api.notification.get<UserNotificationSettings>(`/settings/${userId}`);
    return response.data;
  },

  /** Сохранить per-action настройки. */
  async updateSettings(
    userId: string,
    settings: UserNotificationSettings
  ): Promise<UserNotificationSettings> {
    const response = await api.notification.put<UserNotificationSettings>(
      `/settings/${userId}`,
      settings
    );
    return response.data;
  },
};

export default channelService;
